import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSearchParams } from 'react-router-dom'
import { Box, LinearProgress, useMediaQuery } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CRM_POLL, CrmIntakeView, crmApi } from '../../../features/crm/crmApi'
import { Card, CrmTimer, Empty, Muted, PageHead, Pill, TONES, contentSx, pageSx } from '../shared/crmUi'
import { STEP_NOS, hm, initials, subjectAbbr, subjectTitle, subjectsLine } from '../shared/format'
import { trackDone } from '../shared/stages'
import IntakeDetail, { Check, Legend } from './IntakeDetail'

type Tab = 'NEW' | 'ALL' | 'RETURNED'

const byAsc = (k: keyof CrmIntakeView) => (a: CrmIntakeView, b: CrmIntakeView) => (String(a[k] ?? '') < String(b[k] ?? '') ? -1 : 1)
const byDesc = (k: keyof CrmIntakeView) => (a: CrmIntakeView, b: CrmIntakeView) => (String(a[k] ?? '') < String(b[k] ?? '') ? 1 : -1)

const IntakesPage = () => {
    const { formatMessage, locale } = useIntl()
    const [params, setParams] = useSearchParams()
    const tab = (params.get('tab') as Tab) || 'NEW'
    const selectedParam = Number(params.get('id')) || 0
    const narrow = useMediaQuery('(max-width: 900px)')
    const detailRef = useRef<HTMLDivElement>(null)

    const poll = { pollingInterval: CRM_POLL }
    const newQ = crmApi.useListIntakesQuery({ tab: 'NEW' }, poll)
    const allQ = crmApi.useListIntakesQuery({ tab: 'ALL' }, poll)
    const retQ = crmApi.useListIntakesQuery({ tab: 'RETURNED' }, poll)

    const lists = useMemo(
        () => ({
            NEW: [...(newQ.data ?? [])].sort(byAsc('createdAt')),
            ALL: [...(allQ.data ?? [])].sort(byDesc('approvedAt')),
            RETURNED: [...(retQ.data ?? [])].sort(byDesc('returnedAt')),
        }),
        [newQ.data, allQ.data, retQ.data],
    )
    const items = lists[tab]
    const selectedId = items.some((x) => x.id === selectedParam) ? selectedParam : selectedParam && tab !== 'NEW' ? selectedParam : items[0]?.id || 0

    const go = (next: Tab, id?: number) => setParams(id ? { tab: next, id: String(id) } : { tab: next })

    const [scrollOnSelect, setScrollOnSelect] = useState(false)
    useEffect(() => {
        if (scrollOnSelect && narrow) detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setScrollOnSelect(false)
    }, [selectedId])

    const right = (x: CrmIntakeView) => {
        if (x.status === 'RETURNED') return <Pill tone="bad">{formatMessage({ id: 'crmReturn' })}</Pill>
        if (x.status === 'MODERATION') return <CrmTimer deadlineAt={x.moderatorDeadlineAt} />
        const tracks = x.tracks ?? []
        if (!tracks.length || tracks.every(trackDone)) return <Pill tone="ok">{formatMessage({ id: 'crmFinished' })}</Pill>
        // по каждому предмету: короткое имя + 5 квадратиков
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '3px 6px', alignItems: 'center', justifyItems: 'start' }}>
                {tracks.map((t) => (
                    <React.Fragment key={t.id}>
                        <Box
                            component="span"
                            title={`${subjectTitle(t.subject, locale)}${t.mentorId ? '' : ` · ${formatMessage({ id: 'crmAtHead' })}`}`}
                            sx={{ fontSize: '11.5px', fontWeight: 600, color: t.mentorId ? ui.sub : TONES.wait.text, justifySelf: 'end' }}
                        >
                            {subjectAbbr(t.subject, locale)}
                        </Box>
                        <Box component="span" sx={{ display: 'inline-flex', gap: '2px' }}>
                            {STEP_NOS.map((no) => {
                                const st = t.steps?.find((s) => s.stepNo === no)
                                const kind = st?.verifiedAt ? 'v' : st?.doneAt ? 'u' : ''
                                const state = formatMessage({ id: kind === 'v' ? 'crmLegendVerified' : kind === 'u' ? 'crmLegendUploaded' : 'crmNotUploadedCap' })
                                return <Check key={no} small kind={kind} title={`${no}. ${formatMessage({ id: `crmStep_${no}` })}: ${state}`} />
                            })}
                        </Box>
                    </React.Fragment>
                ))}
            </Box>
        )
    }

    const TABS: Tab[] = ['NEW', 'ALL', 'RETURNED']
    const loading = newQ.isLoading || allQ.isLoading || retQ.isLoading

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead title={formatMessage({ id: 'crmModTitle' })} sub={formatMessage({ id: 'crmModSub' })} />
                {loading && <LinearProgress sx={{ marginBottom: 1 }} />}

                <Box sx={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : 'minmax(280px, 360px) minmax(0, 1fr)', gap: 2, alignItems: 'start' }}>
                    <Card pad={false}>
                        <Box sx={{ display: 'flex', borderBottom: `1px solid ${ui.border}`, overflowX: 'auto' }}>
                            {TABS.map((t) => (
                                <Box
                                    key={t}
                                    component="button"
                                    type="button"
                                    onClick={() => go(t)}
                                    sx={{
                                        flex: 1,
                                        border: 'none',
                                        background: 'transparent',
                                        padding: '12px 10px',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                        fontSize: '13.5px',
                                        whiteSpace: 'nowrap',
                                        fontWeight: tab === t ? 700 : 500,
                                        color: tab === t ? ui.pink : ui.sub,
                                        borderBottom: `2px solid ${tab === t ? ui.pink : 'transparent'}`,
                                    }}
                                >
                                    {formatMessage({ id: `crmModTab_${t}` })} ({lists[t].length})
                                </Box>
                            ))}
                        </Box>
                        {tab === 'ALL' && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', padding: '8px 12px', fontSize: '12px', color: ui.sub, borderBottom: `1px solid ${ui.border}` }}>
                                <Legend kind="" text={formatMessage({ id: 'crmLegendNotDone' })} />
                                <Legend kind="u" text={formatMessage({ id: 'crmLegendMentorUploaded' })} />
                                <Legend kind="v" text={formatMessage({ id: 'crmLegendVerified' })} />
                            </Box>
                        )}
                        <Box sx={{ maxHeight: narrow ? 380 : '70vh', overflowY: 'auto' }}>
                            {!items.length && (
                                <Empty text={formatMessage({ id: tab === 'NEW' ? 'crmNoNew' : tab === 'RETURNED' ? 'crmNoReturns' : 'crmNoStudents' })} />
                            )}
                            {items.map((x) => (
                                <Box
                                    key={x.id}
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        setScrollOnSelect(true)
                                        go(tab, x.id)
                                    }}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '36px minmax(0, 1fr) auto',
                                        gap: '10px',
                                        alignItems: 'center',
                                        width: '100%',
                                        textAlign: 'left',
                                        border: 'none',
                                        borderBottom: `1px solid ${ui.border}`,
                                        background: x.id === selectedId ? ui.pinkSoft : 'transparent',
                                        padding: '10px 12px',
                                        cursor: 'pointer',
                                        font: 'inherit',
                                    }}
                                >
                                    <Box sx={avatar}>{initials(x.data?.studentName)}</Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Box sx={{ fontWeight: 600, fontSize: '13.5px', color: ui.ink, overflowWrap: 'anywhere' }}>
                                            {x.data?.studentName}
                                            {x.editedAfterApprove && <Box component="span" sx={{ color: TONES.wait.text }}> ✎</Box>}
                                        </Box>
                                        <Muted>
                                            {tab === 'ALL' ? '' : `${subjectsLine(x.subjects, locale)} · `}
                                            {hm(x.createdAt)}
                                        </Muted>
                                    </Box>
                                    {right(x)}
                                </Box>
                            ))}
                        </Box>
                    </Card>

                    <Box ref={detailRef} sx={{ minWidth: 0, scrollMarginTop: '12px' }}>
                        {selectedId ? (
                            <IntakeDetail id={selectedId} onMoved={(t, id) => go(t, id)} />
                        ) : (
                            <Card>
                                <Empty text={formatMessage({ id: 'crmPickStudent' })} />
                            </Card>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export const avatar = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: ui.pinkSoft,
    color: ui.pink,
    fontWeight: 700,
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}

export default IntakesPage
